import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useGetSchoolPocsQuery,
  useCreateSchoolPocMutation,
  useUpdateSchoolPocMutation,
} from "../../../../../../../../services/school";
import { schema, FormValues } from "./validations";
import {
  SchoolPoc,
  POC_NAME,
  POC_DESIGNATION,
  POC_DEPARTMENT,
  POC_PHONE,
  POC_EMAIL,
} from "./consts";
import { toast } from "sonner";

export const useAddEditSchoolPoc = () => {
  const { id: schoolId } = useParams<{ id: string }>();
  const [isAddPocDialogOpen, setIsAddPocDialogOpen] = useState(false);
  const [localPocs, setLocalPocs] = useState<SchoolPoc[]>([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  // Fetch existing school POCs
  const { data: schoolPocsData, isLoading: isLoadingPocs } =
    useGetSchoolPocsQuery(schoolId || "", { enabled: !!schoolId });

  const schoolPocs = schoolPocsData?.data || [];

  // Initialize local POCs when schoolPocs data loads
  useEffect(() => {
    if (schoolPocs.length > 0) {
      setLocalPocs(schoolPocs);
    }
  }, [schoolPocs]);

  // Create POC mutation
  const { mutate: createPoc, isPending: isCreatingPoc } =
    useCreateSchoolPocMutation({
      onSuccess: () => {
        toast.success("School POC created successfully");
      },
      onError: (error) => {
        toast.error(
          error.response.data?.issues?.message ||
          error.response.data?.issues?.error ||
            error.response.data.message ||
            "Failed to create POC"
        );
      },
    });

  // Update POCs mutation
  const { mutate: updatePocs, isPending: isUpdatingPocs } =
    useUpdateSchoolPocMutation({
      onSuccess: () => {
        toast.success("POCs updated successfully");
      },
      onError: (error) => {
        toast.error(
          error.response.data?.issues?.message ||
            error.response.data.message ||
            "Failed to update POCs"
        );
      },
    });

  // Form for adding new POC
  const addPocForm = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      pocName: "",
      pocDesignation: "",
      pocDepartment: "",
      pocPhone: "",
      pocEmail: "",
    },
  });

  const handleAddPoc = (values: FormValues) => {
    if (!schoolId) return;

    createPoc({
      schoolId,
      pocList: [
        {
          pocName: values.pocName,
          pocDesignation: values.pocDesignation,
          pocDepartment: values.pocDepartment,
          pocPhone: values.pocPhone,
          pocEmail: values.pocEmail,
        },
      ],
    });
  };

  const handleUpdatePocs = (updatedPocs: SchoolPoc[]) => {
    if (!schoolId) return;

    const pocData = updatedPocs
      .filter((poc) => poc.id) // Ensure poc.id exists
      .map((poc) => ({
        pocId: poc.id!, // All POCs here are existing ones with valid IDs
        pocName: poc.pocName,
        pocDesignation: poc.pocDesignation,
        pocDepartment: poc.pocDepartment,
        pocPhone: poc.pocPhone,
        pocEmail: poc.pocEmail,
      }));

    updatePocs({
      schoolId,
      data: { pocList: pocData },
    });
  };

  // Validation using Zod
  const validatePocData = (poc: SchoolPoc): boolean => {
    try {
      const pocData = {
        [POC_NAME]: poc.pocName,
        [POC_DESIGNATION]: poc.pocDesignation,
        [POC_DEPARTMENT]: poc.pocDepartment || "",
        [POC_PHONE]: poc.pocPhone,
        [POC_EMAIL]: poc.pocEmail,
      };

      schema.parse(pocData);
      return true;
    } catch (error: any) {
      const errors: { [key: string]: string } = {};

      if (error.errors) {
        error.errors.forEach((err: any) => {
          const field = err.path[0];
          errors[`${poc.id}-${field}`] = err.message;
        });
      }

      setValidationErrors((prev) => ({ ...prev, ...errors }));
      return false;
    }
  };

  const validateAllPocs = (): boolean => {
    setValidationErrors({}); // Clear previous errors
    const allValid = localPocs.every((poc) => validatePocData(poc));
    return allValid;
  };

  const handleSaveChanges = () => {
    console.log("Save button clicked, localPocs:", localPocs);

    // Validate all POCs before saving
    if (!validateAllPocs()) {
      console.log("Validation failed, not saving");
      return; // Don't save if validation fails
    }

    console.log("Validation passed, proceeding with save");

    // Separate new and existing POCs
    const newPocs = localPocs.filter((poc) => poc.id?.startsWith("temp-"));
    const existingPocs = localPocs.filter(
      (poc) => poc.id && !poc.id.startsWith("temp-")
    );

    console.log("New POCs:", newPocs);
    console.log("Existing POCs:", existingPocs);

    // Create new POCs
    if (newPocs.length > 0) {
      console.log("Creating new POCs:", newPocs);
      const pocData = newPocs.map((poc) => ({
        pocName: poc.pocName,
        pocDesignation: poc.pocDesignation,
        pocDepartment: poc.pocDepartment,
        pocPhone: poc.pocPhone,
        pocEmail: poc.pocEmail,
      }));

      createPoc({
        schoolId: schoolId || "",
        pocList: pocData,
      });
    }

    // Update existing POCs
    if (existingPocs.length > 0) {
      console.log("Updating existing POCs");
      handleUpdatePocs(existingPocs);
    } else {
      console.log("No existing POCs to update");
    }
  };

  const handleAddNewRow = () => {
    setIsAddingNew(true);
    const newPoc: SchoolPoc = {
      id: `temp-${Date.now()}`, // Temporary ID for new entries
      pocName: "",
      pocDesignation: "",
      pocDepartment: "",
      pocPhone: "",
      pocEmail: "",
      schoolId: "", // Will be set when saving
    };
    setLocalPocs([...localPocs, newPoc]);
  };

  const handleDeletePocLocal = (pocId: string) => {
    setLocalPocs(localPocs.filter((poc) => poc.id !== pocId));
    if (pocId.startsWith("temp-")) {
      // If it's a temporary entry, just remove from local state
      return;
    }
    // If it's an existing entry, also delete from server
    handleDeletePoc(pocId);
  };

  const handlePocFieldChange = (
    pocId: string,
    field: string,
    value: string
  ) => {

    const updatedPocs = localPocs.map((poc) => {
      if (poc.id === pocId) {
        return { ...poc, [field]: value };
      }
      return poc;
    });

    setLocalPocs(updatedPocs);
  };

  const handleCancelNewRow = () => {
    // Remove the temporary row
    setLocalPocs(localPocs.filter((poc) => !poc.id?.startsWith("temp-")));
    setIsAddingNew(false);
  };

  const handleDeletePoc = (pocId: string) => {
    const updatedPocs = schoolPocs.filter((poc) => poc.id !== pocId);
    handleUpdatePocs(updatedPocs);
  };

  const openAddPocDialog = () => {
    addPocForm.reset();
    setIsAddPocDialogOpen(true);
  };

  const closeAddPocDialog = () => {
    setIsAddPocDialogOpen(false);
    addPocForm.reset();
  };

  return {
    schoolPocs,
    isLoadingPocs,
    isAddPocDialogOpen,
    addPocForm,
    handleAddPoc,
    handleUpdatePocs,
    handleDeletePoc,
    openAddPocDialog,
    closeAddPocDialog,
    isCreatingPoc,
    isUpdatingPocs,
    // New local state and handlers
    localPocs,
    isAddingNew,
    validationErrors,
    handleSaveChanges,
    handleAddNewRow,
    handleDeletePocLocal,
    handlePocFieldChange,
    handleCancelNewRow,
  };
};
